import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ENC } from "@/constants/testIds";
import { searchDiagnosisCodes } from "@/lib/diagnosisApi";
import { catalogItemToDiagnosis } from "@/lib/encounterDiagnoses";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  return "Unable to search diagnosis codes.";
}

export default function DiagnosisSearchPanel({
  selectedCodes = [],
  onSelect,
  readOnly,
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if (!debounced) {
      setItems([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    searchDiagnosisCodes({ q: debounced, active: true, limit: 25 })
      .then((data) => {
        if (cancelled) return;
        setItems((data.items || []).map(catalogItemToDiagnosis).filter((row) => row.code));
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setError(apiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const selected = new Set(selectedCodes);

  return (
    <div data-testid={ENC.screenDiagnosisSearch} className="relative z-10 space-y-3">
      <div className="relative z-10">
        <Search className="pointer-events-none absolute left-3 top-1/2 z-0 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          type="search"
          name="diagnosis-lookup"
          autoComplete="off"
          spellCheck={false}
          data-testid={ENC.screenDiagnosisSearchInput}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder="Search ICD-10-CM by code or description…"
          className="relative z-10 h-10 bg-white pl-10 text-sm"
          autoFocus
          disabled={readOnly}
        />
      </div>

      {error ? (
        <p data-testid={ENC.screenDiagnosisSearchError} className="py-6 text-center text-sm text-rose-600">
          {error}
        </p>
      ) : loading ? (
        <div
          data-testid={ENC.screenDiagnosisSearchLoading}
          className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Searching ICD-10-CM…
        </div>
      ) : !debounced ? (
        <p className="py-6 text-center text-sm text-slate-500">
          Type a diagnosis, symptom, or ICD-10 code to search the master list.
        </p>
      ) : !items.length ? (
        <p data-testid={ENC.screenDiagnosisSearchEmpty} className="py-6 text-center text-sm text-slate-500">
          No diagnosis codes match “{debounced}”.
        </p>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          <DiagnosisPickList
            items={items}
            selectedCodes={selected}
            onSelect={onSelect}
            readOnly={readOnly}
            countLabel={`${items.length} result${items.length === 1 ? "" : "s"}`}
          />
        </div>
      )}
    </div>
  );
}

export function DiagnosisPickList({
  items,
  selectedCodes,
  onSelect,
  readOnly,
  countLabel,
  addTestId = ENC.screenDiagnosisResult,
}) {
  return (
    <div>
      {countLabel ? <p className="mb-2 text-xs font-medium text-slate-500">{countLabel}</p> : null}
      <ul data-testid={ENC.screenDiagnosisResults} className="divide-y divide-slate-100 rounded-md border border-slate-100">
        {items.map((row) => {
          const added = selectedCodes.has(row.code);
          return (
            <li key={row.code} className="flex items-center gap-2 px-3 py-1.5">
              <span className="shrink-0 font-fhir text-sm font-medium text-sky-800">{row.code}</span>
              <span className="min-w-0 flex-1 text-sm leading-snug text-slate-800">{row.description}</span>
              {added ? (
                <span
                  data-testid={`${addTestId}-${row.code}-added`}
                  className="w-14 shrink-0 text-right text-xs font-semibold text-emerald-700"
                >
                  Added
                </span>
              ) : (
                <button
                  type="button"
                  data-testid={`${addTestId}-${row.code}`}
                  disabled={readOnly}
                  onClick={() => onSelect?.(row)}
                  className="w-14 shrink-0 rounded-md bg-sky-50 py-0.5 text-center text-xs font-semibold text-sky-800 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Add
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
